// SettingsSurface.tsx — /settings, the merged Account + Privacy + Team surface (overhaul §5.5 / Phase 7).
//
// One warm-reskinned surface that replaces three legacy pages (AccountSurface, TrustSurface,
// TeamSettingsSurface). Three sections:
//   • Account — email, theme (plan/billing/sessions removed — no backend yet)
//   • Privacy — private-conversation manager (see/un-private), redaction filters, capture status, delete
//   • Team    — name, your role, admin-only rename / delete / leave (D8: role gates actions, not pages)
// Member management (roster / invite / profile editor) is NOT here — it lives in People (Phase 5).
//
// Reuse-don't-rebuild: the data comes from the existing hooks (useTrustSettings,
// useTeamSettings + its mutations); this file owns the warm chrome (ov-surface → .wrap → masthead →
// .card rows) and re-skins the salvaged delete / rename state machines.

import { useState, useEffect, useRef, useCallback, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api';
import { supabase } from '../../lib/auth';
import { DeletionResidueNotice } from '../DeletionResidueNotice';
import { SITE_COLOR, siteLabel } from '../../lib/sites';
import { useTrustSettings } from '../../hooks/useTrustSettings';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useUserLocation, useSaveLocation } from '../../hooks/useUserLocation';
import { useRenameTeam, useDeleteTeam, useRemoveMember } from '../../hooks/useTeamSettings';
import type { Theme } from '../../lib/theme';
import type { TeamSettingsData } from '../../hooks/useTeamSettings';

interface SettingsSurfaceProps {
  teamData: TeamSettingsData;
  currentUserId: string;
  userEmail?: string;
  theme: Theme;
  onToggleTheme: () => void;
  onNav: (id: string) => void;
}

const CRUMB = 'ACCOUNT · SETTINGS';

// ─── Shared warm chrome (the .dgroup/.drow/.dk/.dv classes are .drawer-scoped, so settings rows
// are built from warm tokens directly here). ───

function Masthead() {
  return (
    <div className="shead">
      <div>
        <div className="crumb">{CRUMB}</div>
        <h1 className="greeting">Your account, <span className="em">your rules</span>.</h1>
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, margin: '38px 0 14px',
      fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: '.14em',
      textTransform: 'uppercase', color: 'var(--faint)',
    }}>
      <span>{children}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--hair)' }} />
    </div>
  );
}

// One label/value row inside a .card. last=true drops the divider. On a phone the row STACKS
// (label+sub above, value below) — side-by-side at 390px either clipped rigid values
// (the Capturing chip, Open People) or crushed them (the Location editor).
function Row({ label, sub, last, children }: {
  label: string; sub?: string; last?: boolean; children?: ReactNode;
}) {
  const isMobile = useIsMobile();
  return (
    <div style={{
      display: 'flex', flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between',
      gap: isMobile ? 10 : 16,
      padding: '15px 20px', borderBottom: last ? 'none' : '1px solid var(--hair)',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: 'var(--ink)' }}>{label}</div>
        {sub && <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
      </div>
      {/* minWidth:0 (not flexShrink:0) so a long value — e.g. the team name — ellipsizes
          instead of dragging the row past the viewport (bug-720 class). */}
      <div style={{ fontSize: 13, color: 'var(--inkS)', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
        {children}
      </div>
    </div>
  );
}

const VALUE_MONO: CSSProperties = { fontFamily: "'DM Mono', monospace", fontSize: 12.5, color: 'var(--inkB)' };

// ─── Account section ───

// Location — the city the daily brief's masthead weather tile forecasts against. The saved city is
// geocoded server-side (POST /api/surfaces/trust/location); a blank save clears it and the tile
// falls back to the Google-calendar timezone. A 422 means the typed city didn't resolve — shown as
// a friendly prompt, distinct from a real failure.
function LocationRow() {
  const { data, isLoading } = useUserLocation();
  const save = useSaveLocation();
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const current = data?.location ?? null;

  const submit = async () => {
    const q = query.trim();
    if (!q || save.isPending) return;
    setError(null);
    try {
      const res = await save.mutateAsync(q);
      if (res.location) setQuery('');
      else setError("We couldn't find that city.");
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 422
          ? "We couldn't find that city - try adding a state or country."
          : 'Something went wrong. Please try again.'
      );
    }
  };

  const clear = async () => {
    if (save.isPending) return;
    setError(null);
    try {
      await save.mutateAsync('');
      setQuery('');
    } catch {
      setError('Something went wrong. Please try again.');
    }
  };

  const canSave = Boolean(query.trim()) && !save.isPending;
  const isMobile = useIsMobile();

  return (
    <Row label="Location" sub="Sets the city for your daily brief's weather tile.">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: isMobile ? 'stretch' : 'flex-end', minWidth: 0, width: isMobile ? '100%' : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submit(); }}
            placeholder={current?.label ? 'Change city…' : 'City (e.g. Boston)'}
            style={{
              background: 'none', border: '1px solid var(--hair2)', borderRadius: 8, outline: 'none',
              fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--ink)', padding: '6px 10px',
              width: isMobile ? '100%' : 176, minWidth: 0,
            }}
          />
          <button
            onClick={submit}
            disabled={!canSave}
            style={{
              background: 'none', border: 'none', cursor: canSave ? 'pointer' : 'default',
              fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: '.12em',
              textTransform: 'uppercase', color: 'var(--acc)', opacity: canSave ? 1 : 0.4, padding: 0, flexShrink: 0,
            }}
          >{save.isPending ? 'Saving…' : 'Save'}</button>
        </div>
        <div style={{ fontSize: 11.5, lineHeight: 1.45, textAlign: isMobile ? 'left' : 'right' }}>
          {error ? (
            <span style={{ color: 'var(--rose)' }}>{error}</span>
          ) : current?.label ? (
            <span style={{ color: 'var(--faint)' }}>
              Weather for {current.label}
              {' · '}
              <button
                onClick={clear}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--acc)', cursor: 'pointer', font: 'inherit' }}
              >Clear</button>
            </span>
          ) : isLoading ? null : (
            <span style={{ color: 'var(--faint)' }}>Using your calendar&rsquo;s timezone.</span>
          )}
        </div>
      </div>
    </Row>
  );
}

function AccountSection({ userEmail, theme, onToggleTheme }: {
  userEmail?: string;
  theme: Theme;
  onToggleTheme: () => void;
}) {
  const setTheme = (next: Theme) => { if (theme !== next) onToggleTheme(); };

  // Plan / billing history / connected sessions were removed: no billing or session backend
  // exists yet, so the GET returned a hardcoded plan + empty tables. Re-add when those land.
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <Row label="Email">
        <span style={{ ...VALUE_MONO, minWidth: 0, overflowWrap: 'anywhere' }}>{userEmail || '-'}</span>
      </Row>

      <LocationRow />

      <Row label="Appearance" sub="Light or dark theme for the dashboard." last>
        <span style={{ display: 'flex', gap: 6 }}>
          <button
            className={`chip ${theme === 'light' ? 'on' : ''}`}
            onClick={() => setTheme('light')}
            aria-pressed={theme === 'light'}
          >☀ Light</button>
          <button
            className={`chip ${theme === 'dark' ? 'on' : ''}`}
            onClick={() => setTheme('dark')}
            aria-pressed={theme === 'dark'}
          >☾ Dark</button>
        </span>
      </Row>
    </div>
  );
}

// ─── Privacy section ───

// Small confirm-dialog scaffolding (warm tokens; D10 keeps destructive actions small dialogs).
const DIALOG_BACKDROP: CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(10,8,4,.36)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 90,
};
const DIALOG_CARD: CSSProperties = {
  position: 'relative', background: 'var(--panel)', border: '1px solid var(--hair2)',
  borderRadius: 14, width: '90%', maxWidth: 440, padding: 24, boxShadow: 'var(--sig-shadow)',
};
const ROSE_BTN: CSSProperties = {
  color: 'var(--rose)', borderColor: 'color-mix(in srgb, var(--rose) 40%, transparent)',
};
const ERROR_BANNER: CSSProperties = {
  background: 'color-mix(in srgb, var(--rose) 8%, transparent)',
  border: '1px solid color-mix(in srgb, var(--rose) 28%, transparent)',
  borderRadius: 10, padding: '10px 14px', marginTop: 12, fontSize: 12.5, color: 'var(--rose)',
};


const DEFAULT_REDACTION_PATTERNS = [
  'API keys, tokens, secrets, passwords',
  'Email addresses',
  'Phone numbers',
  'SSNs, government IDs',
  'Credit card / bank account numbers',
  'Physical street addresses',
];

type RedactionPattern = { name: string; checked: boolean };

// Redaction patterns — defaults merged with the user's saved set; optimistic save with rollback
// on failure (no silent swallow). Endpoint contract unchanged from the legacy Trust surface.
function RedactionCard() {
  const [patterns, setPatterns] = useState<RedactionPattern[]>([]);
  const [newPattern, setNewPattern] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    apiFetch<{ patterns?: RedactionPattern[] }>('/api/surfaces/trust/redaction-patterns')
      .then(res => {
        const saved = res?.patterns ?? [];
        const savedNames = new Set(saved.map(p => p.name));
        const defaults = DEFAULT_REDACTION_PATTERNS
          .filter(n => !savedNames.has(n))
          .map(n => ({ name: n, checked: true }));
        setPatterns([...defaults, ...saved]);
      })
      .catch(() => setPatterns(DEFAULT_REDACTION_PATTERNS.map(n => ({ name: n, checked: true }))))
      .finally(() => setLoaded(true));
  }, []);

  const save = useCallback(async (next: RedactionPattern[]) => {
    let prev: RedactionPattern[] = [];
    setPatterns(curr => { prev = curr; return next; });
    try {
      await apiFetch('/api/surfaces/trust/redaction-patterns', {
        method: 'PUT', body: JSON.stringify({ patterns: next }),
      });
    } catch {
      console.warn('[settings/privacy] redaction save failed - rolling back');
      setPatterns(prev);
    }
  }, []);

  const toggle = (idx: number) => save(patterns.map((p, i) => i === idx ? { ...p, checked: !p.checked } : p));
  const remove = (idx: number) => save(patterns.filter((_, i) => i !== idx));
  const add = () => {
    const trimmed = newPattern.trim();
    if (!trimmed) return;
    save([...patterns, { name: trimmed, checked: true }]);
    setNewPattern('');
  };
  const isDefault = (name: string) => DEFAULT_REDACTION_PATTERNS.includes(name);

  if (!loaded) return null;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 2px 8px',
        fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: '.12em',
        textTransform: 'uppercase', color: 'var(--faint)',
      }}>
        <span>Redaction patterns</span>
        <span>{patterns.filter(p => p.checked).length} of {patterns.length} active</span>
      </div>
      <div className="card" style={{ padding: '6px 20px' }}>
        {patterns.map((pat, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0',
            borderBottom: '1px solid var(--hair)',
          }}>
            <span style={{ flex: 1, fontSize: 13, color: pat.checked ? 'var(--ink)' : 'var(--faint)' }}>{pat.name}</span>
            {!isDefault(pat.name) && (
              <button
                onClick={() => remove(i)}
                aria-label={`Remove ${pat.name}`}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)', fontSize: 15, lineHeight: 1, padding: '0 4px' }}
              >×</button>
            )}
            <span className={`toggle ${pat.checked ? 'on' : ''}`} role="switch" aria-checked={pat.checked} aria-label={pat.name} onClick={() => toggle(i)} />
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 0' }}>
          <input
            type="text"
            value={newPattern}
            onChange={e => setNewPattern(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') add(); }}
            placeholder="Add a custom pattern…"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: 'var(--ink)' }}
          />
          <button
            onClick={add}
            disabled={!newPattern.trim()}
            style={{
              background: 'none', border: 'none', cursor: newPattern.trim() ? 'pointer' : 'default',
              fontFamily: "'DM Mono', monospace", fontSize: 11, letterSpacing: '.12em',
              textTransform: 'uppercase', color: 'var(--acc)', opacity: newPattern.trim() ? 1 : 0.4, padding: 0,
            }}
          >+ Add</button>
        </div>
      </div>
    </div>
  );
}

// Delete-all-data: wipes data, keeps the account. Reuses the existing /api/account/delete-data
// endpoint + DeletionResidueNotice.
function DeleteDataDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'confirm' | 'deleting' | 'done'>('confirm');
  const [error, setError] = useState('');

  const handle = async () => {
    setStep('deleting'); setError('');
    try {
      await apiFetch<{ deleted: boolean }>('/api/account/delete-data', { method: 'POST' });
      setStep('done');
      qc.invalidateQueries();
    } catch {
      setError('Failed to delete data. Please try again.');
      setStep('confirm');
    }
  };

  return (
    <div onClick={step === 'deleting' ? undefined : onClose} style={DIALOG_BACKDROP}>
      <div onClick={e => e.stopPropagation()} style={{ ...DIALOG_CARD, borderColor: 'color-mix(in srgb, var(--rose) 30%, transparent)' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--rose)', marginBottom: 12 }}>Delete all data</div>
        {step === 'done' ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--grn)', lineHeight: 1.55, marginBottom: 18 }}>All data deleted. Your account is still active.</div>
            <button className="btn btn-secondary sm" style={{ width: '100%', justifyContent: 'center' }} onClick={onClose}>Done</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--inkS)', lineHeight: 1.6, marginBottom: 14 }}>
              Permanently delete all conversations, memories, knowledge graph, and settings. Your account stays active so you can start fresh.
            </div>
            <DeletionResidueNotice />
            {error && <div style={{ fontSize: 12.5, color: 'var(--rose)', marginTop: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="btn btn-secondary sm" disabled={step === 'deleting'} style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
              <button className="btn btn-secondary sm" disabled={step === 'deleting'} style={{ flex: 1, justifyContent: 'center', ...ROSE_BTN, opacity: step === 'deleting' ? 0.6 : 1 }} onClick={handle}>
                {step === 'deleting' ? 'Deleting…' : 'Delete data'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Delete-account: permanent. Text-match gate ("delete my account") + DeletionResidueNotice; on
// success signs out and redirects (reuses /api/account/delete).
function DeleteAccountDialog({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<'confirm' | 'deleting' | 'done'>('confirm');
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const armed = text === 'delete my account';

  const handle = async () => {
    setStep('deleting'); setError('');
    try {
      await apiFetch<{ deleted: boolean }>('/api/account/delete', { method: 'POST' });
      setStep('done');
      setTimeout(async () => { await supabase.auth.signOut(); window.location.href = '/'; }, 3000);
    } catch {
      setError('Failed to delete account. Please try again.');
      setStep('confirm');
    }
  };

  const busy = step === 'deleting' || step === 'done';

  return (
    <div onClick={busy ? undefined : onClose} style={DIALOG_BACKDROP}>
      <div onClick={e => e.stopPropagation()} style={{ ...DIALOG_CARD, borderColor: 'color-mix(in srgb, var(--rose) 30%, transparent)' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--rose)', marginBottom: 12 }}>Delete account</div>
        {step === 'done' ? (
          <div style={{ fontSize: 13, color: 'var(--grn)', lineHeight: 1.55 }}>Account deleted. Redirecting…</div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--inkS)', lineHeight: 1.6, marginBottom: 14 }}>
              Permanently delete your account and all associated data. This cannot be undone.
            </div>
            <DeletionResidueNotice mode="account" />
            <div style={{ fontSize: 13, color: 'var(--inkS)', margin: '14px 0 6px' }}>
              Type <strong style={{ color: 'var(--inkB)' }}>delete my account</strong> to confirm:
            </div>
            <input
              type="text"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="delete my account"
              autoFocus
              style={{
                width: '100%', padding: '8px 12px', background: 'var(--bg)',
                border: '1px solid var(--hair2)', borderRadius: 8, outline: 'none',
                color: 'var(--ink)', fontFamily: "'DM Sans', sans-serif", fontSize: 13,
              }}
            />
            {error && <div style={{ fontSize: 12.5, color: 'var(--rose)', marginTop: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button className="btn btn-secondary sm" disabled={step === 'deleting'} style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
              <button
                className="btn btn-secondary sm"
                disabled={!armed || step === 'deleting'}
                style={{ flex: 1, justifyContent: 'center', ...ROSE_BTN, opacity: armed && step !== 'deleting' ? 1 : 0.5, cursor: armed ? 'pointer' : 'default' }}
                onClick={handle}
              >{step === 'deleting' ? 'Deleting…' : 'Delete everything'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// "Private conversations" manager — the user's private convos with a one-click "Make public".
// Ported from the old Trust surface, re-skinned warm. Backend already wired: GET
// /api/surfaces/conversations (personal; returns private + sensitivity_reason) lists them, and
// PATCH /api/conversations/:id/privacy { private:false } un-privates (which also prunes the
// cross-user similarity edges server-side, bug-150).
interface PrivConvItem {
  id: string;
  conversation_title: string | null;
  topic: string;
  site: string;
  private: boolean;
  sensitivity_reason: string | null;
}

// Every private row should say WHY it's private. Two distinct causes, shown as two tones:
//  - 'flagged'  → the distiller's LLM judged the CONTENT sensitive (salary/health/legal/…).
//                 sensitivity_reason holds the category. Shown amber (a content flag).
//  - 'default'  → private by a structural rule, not a content judgement: emails & Slack threads
//                 are connecting-user-only by design, and Google Docs you don't own are private
//                 too (email-distiller/thread-distiller hardcode private=true; doc-compactor ORs
//                 in !is_owned). These carry NO sensitivity_reason — null is expected, not a gap.
//                 'manual' (you marked it) is also structural. Shown muted (informational), so the
//                 UI never implies an ordinary email was specially flagged.
// Human labels for the LLM sensitivity taxonomy (bug-313). Mirrors the backend's
// SENSITIVITY_CATEGORIES (MVP lib/memory/conversation-compactor.ts); the snake_case humanizer
// fallback below covers 'manual' (handled separately) and legacy free-text reasons from before
// the enum was enforced (e.g. 'academic', 'chore', 'proprietary_product').
const REASON_LABELS: Record<string, string> = {
  salary: 'Salary & compensation',
  personal_finance: 'Personal finance',
  health: 'Health',
  job_search: 'Job search',
  legal: 'Legal',
  personal_relationship: 'Personal relationship',
  personal_other: 'Personal',
  personnel: 'Personnel',
  security: 'Security & credentials',
  confidential_business: 'Confidential business',
};

function privacyReason(site: string, reason: string | null): { text: string; tone: 'flagged' | 'default' } {
  if (reason && reason !== 'manual') return { text: REASON_LABELS[reason] ?? reason.replace(/_/g, ' '), tone: 'flagged' };
  if (reason === 'manual') return { text: 'You made this private', tone: 'default' };
  // reason == null → derive the structural cause from the source.
  if (site === 'email' || site === 'slack') return { text: 'Private by default', tone: 'default' };
  if (site === 'google-docs') return { text: 'Shared doc · not yours', tone: 'default' };
  return { text: 'Private', tone: 'default' };
}

function PrivateConversationsCard({ onNav }: { onNav: (id: string) => void }) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();
  const { data, isLoading } = useQuery({
    queryKey: ['surfaces', 'conversations', 'private'],
    queryFn: () => apiFetch<{ conversations: PrivConvItem[] }>('/api/surfaces/conversations?limit=500'),
    staleTime: 60 * 1000,
  });
  const [toast, setToast] = useState<PrivConvItem | null>(null);
  const toastTimer = useRef<number | null>(null);
  const dismissToast = useCallback(() => {
    if (toastTimer.current) { clearTimeout(toastTimer.current); toastTimer.current = null; }
    setToast(null);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const makePublic = useMutation({
    mutationFn: (c: PrivConvItem) =>
      apiFetch(`/api/conversations/${c.id}/privacy`, { method: 'PATCH', body: JSON.stringify({ private: false }) }),
    onSuccess: (_data, c) => {
      qc.invalidateQueries({ queryKey: ['surfaces', 'conversations', 'private'] });
      // Confirm the real, server-persisted flip with an undoable toast.
      if (toastTimer.current) clearTimeout(toastTimer.current);
      setToast(c);
      toastTimer.current = window.setTimeout(() => { setToast(null); toastTimer.current = null; }, 8000);
    },
  });

  // Undo re-privates the conversation; invalidating the query brings the row back into the list.
  const undoPublic = useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/conversations/${id}/privacy`, { method: 'PATCH', body: JSON.stringify({ private: true }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surfaces', 'conversations', 'private'] }),
  });

  const privates = (data?.conversations ?? []).filter(c => c.private);

  return (
    <>
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid var(--hair)' }}>
        <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 500 }}>Private conversations</div>
        <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: '.1em', textTransform: 'uppercase', color: privates.length ? 'var(--acc)' : 'var(--faint)' }}>
          {privates.length} hidden from team
        </div>
      </div>
      {isLoading ? (
        <div style={{ padding: '18px 20px', fontSize: 13, color: 'var(--faint)' }}>Loading…</div>
      ) : privates.length === 0 ? (
        <div style={{ padding: '18px 20px', fontSize: 13, color: 'var(--faint)', lineHeight: 1.5 }}>
          No private conversations - everything you&rsquo;ve captured is visible to your team.
        </div>
      ) : (
        privates.map((c, i) => {
          const busy = makePublic.isPending && makePublic.variables?.id === c.id;
          const reason = privacyReason(c.site, c.sensitivity_reason);
          return (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px', borderBottom: i < privates.length - 1 ? '1px solid var(--hair)' : 'none' }}>
              <span className="dot" style={{ background: SITE_COLOR[c.site] ?? 'var(--faint)', flexShrink: 0 }} />
              <div
                style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                onClick={() => onNav(`my-conversations/${c.id}`)}
                title="Open conversation"
              >
                <div style={{ fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.conversation_title || c.topic || 'Untitled'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3 }}>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--faint)' }}>{siteLabel(c.site)}</span>
                  <span className="lab" style={reason.tone === 'flagged'
                    ? { fontSize: 9, color: 'var(--amb)', borderColor: 'color-mix(in srgb, var(--amb) 35%, transparent)', textTransform: 'capitalize' }
                    : { fontSize: 9, color: 'var(--faint)', borderColor: 'var(--hair)' }}>
                    {reason.text}
                  </span>
                </div>
              </div>
              <button className="btn btn-secondary sm" disabled={busy} style={{ flexShrink: 0, opacity: busy ? 0.6 : 1 }} onClick={() => makePublic.mutate(c)}>
                {busy ? 'Saving…' : 'Make public'}
              </button>
            </div>
          );
        })
      )}
    </div>
    {toast && createPortal(
      <>
        <style>{`@keyframes terumPrivToastIn{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>
        {/* Phone: lift the toast above the fixed bottom tab bar (same 64px clearance .v2-main
            reserves for it) — at bottom:24 the undo affordance hid behind the bar. */}
        <div role="status" aria-live="polite" className="card" style={{
          position: 'fixed', bottom: isMobile ? 'calc(64px + env(safe-area-inset-bottom, 0px) + 12px)' : 24,
          right: isMobile ? 12 : 24, zIndex: 9999,
          width: 340, maxWidth: 'calc(100vw - 48px)', padding: '14px 16px',
          display: 'flex', alignItems: 'flex-start', gap: 12,
          boxShadow: '0 12px 44px rgba(0,0,0,0.18)',
          animation: 'terumPrivToastIn 0.22s ease-out',
        }}>
          <span className="dot" style={{ background: 'var(--acc)', flexShrink: 0, marginTop: 6 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--ink)', lineHeight: 1.5 }}>
              You just made <strong style={{ fontWeight: 600 }}>{toast.conversation_title || toast.topic || 'Untitled'}</strong> viewable to your entire team.
            </div>
            <button
              onClick={() => { const id = toast.id; dismissToast(); undoPublic.mutate(id); }}
              disabled={undoPublic.isPending}
              style={{
                marginTop: 8, background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: '.12em',
                textTransform: 'uppercase', color: 'var(--acc)', opacity: undoPublic.isPending ? 0.6 : 1,
              }}
            >{undoPublic.isPending ? 'Undoing…' : 'Undo'}</button>
          </div>
          <button onClick={dismissToast} aria-label="Dismiss" style={{
            background: 'none', border: 'none', padding: 2, margin: '-2px -2px 0 0',
            cursor: 'pointer', color: 'var(--faint)', lineHeight: 0, flexShrink: 0,
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M3 3l8 8M11 3l-8 8" /></svg>
          </button>
        </div>
      </>,
      document.body
    )}
    </>
  );
}

function PrivacySection({ onNav }: { onNav: (id: string) => void }) {
  const { data: trust } = useTrustSettings();
  const [deleteMode, setDeleteMode] = useState<null | 'account' | 'data'>(null);
  const capturePaused = trust?.capture_paused ?? false;

  return (
    <>
      {/* See your private conversations + make any public (one-click un-private). */}
      <PrivateConversationsCard onNav={onNav} />

      {/* Redaction filters — what gets scrubbed before storage. */}
      <RedactionCard />

      <div className="card" style={{ overflow: 'hidden', marginTop: 16 }}>
        <Row
          label="Capture"
          sub={capturePaused ? 'Paused - reconnect a source on Sources to resume.' : 'New conversations from your connected sources are being captured.'}
          last
        >
          <span style={{
            display: 'inline-flex', flexShrink: 0, alignItems: 'center', gap: 7,
            color: capturePaused ? 'var(--rose)' : 'var(--grn)',
            fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: capturePaused ? 'var(--rose)' : 'var(--grn)' }} />
            {capturePaused ? 'Paused' : 'Capturing'}
          </span>
        </Row>
      </div>

      <div className="danger-zone" style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--rose)', marginBottom: 4 }}>Delete all data</div>
          <div style={{ fontSize: 12.5, color: 'var(--inkS)', lineHeight: 1.5, marginBottom: 10 }}>
            Wipe all conversations, memories, and knowledge graph. Your account stays active so you can start fresh.
          </div>
          <button className="btn btn-secondary sm" style={ROSE_BTN} onClick={() => setDeleteMode('data')}>Delete data</button>
        </div>
        <div style={{ borderTop: '1px solid color-mix(in srgb, var(--rose) 18%, transparent)', paddingTop: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--rose)', marginBottom: 4 }}>Delete account</div>
          <div style={{ fontSize: 12.5, color: 'var(--inkS)', lineHeight: 1.5, marginBottom: 10 }}>
            Permanently delete your account and all associated data. This cannot be undone.
          </div>
          <button className="btn btn-secondary sm" style={ROSE_BTN} onClick={() => setDeleteMode('account')}>Delete account</button>
        </div>
      </div>

      {deleteMode === 'data' && <DeleteDataDialog onClose={() => setDeleteMode(null)} />}
      {deleteMode === 'account' && <DeleteAccountDialog onClose={() => setDeleteMode(null)} />}
    </>
  );
}

// ─── Team section (identity + lifecycle, admin-gated — D8) ───

// Leave / delete confirm dialog. Both routes guarded by the caller (canLeave / canDelete); on
// success the user no longer belongs here, so we navigate back to Briefing.
function TeamDangerDialog({ mode, teamId, currentUserId, onNav, onClose }: {
  mode: 'leave' | 'delete'; teamId: string; currentUserId: string; onNav: (id: string) => void; onClose: () => void;
}) {
  const deleteTeam = useDeleteTeam();
  const leave = useRemoveMember();
  const [error, setError] = useState('');
  const isPending = deleteTeam.isPending || leave.isPending;
  const isDelete = mode === 'delete';

  const handle = () => {
    setError('');
    const onSuccess = () => { onClose(); onNav('briefing'); };
    const onError = (err: unknown) => setError(err instanceof Error ? err.message : `Failed to ${mode} team`);
    if (isDelete) deleteTeam.mutate(teamId, { onSuccess, onError });
    else leave.mutate({ teamId, userId: currentUserId }, { onSuccess, onError });
  };

  return (
    <div onClick={isPending ? undefined : onClose} style={DIALOG_BACKDROP}>
      <div onClick={e => e.stopPropagation()} style={{ ...DIALOG_CARD, borderColor: 'color-mix(in srgb, var(--rose) 30%, transparent)' }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: 'var(--rose)', marginBottom: 12 }}>{isDelete ? 'Delete team' : 'Leave team'}</div>
        <div style={{ fontSize: 13, color: 'var(--inkS)', lineHeight: 1.6, marginBottom: 14 }}>
          {isDelete
            ? 'Permanently delete this team and remove all members. This cannot be undone.'
            : 'You’ll need a new invite to rejoin, and your conversations will no longer be shared with this team.'}
        </div>
        <DeletionResidueNotice />
        {error && <div style={{ fontSize: 12.5, color: 'var(--rose)', marginTop: 10 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button className="btn btn-secondary sm" disabled={isPending} style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary sm" disabled={isPending} style={{ flex: 1, justifyContent: 'center', ...ROSE_BTN, opacity: isPending ? 0.6 : 1 }} onClick={handle}>
            {isPending ? (isDelete ? 'Deleting…' : 'Leaving…') : (isDelete ? 'Delete team' : 'Leave team')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Team lifecycle capabilities (D8 + mandatory-teams model). Pure + exported so the role-gating
// matrix is unit-testable without rendering: a personal/solo team can't be left or deleted (it
// just resurrects / the backend 400s), and a sole admin can't leave (promote someone first).
export interface TeamCaps {
  isAdmin: boolean;
  isSoleMember: boolean;
  isSoleAdmin: boolean;
  canLeave: boolean;
  canDelete: boolean;
}
export function computeTeamCaps(members: Array<{ user_id: string; role: string }>, currentUserId: string): TeamCaps {
  const me = members.find(m => m.user_id === currentUserId);
  const isAdmin = me?.role === 'admin';
  const isSoleMember = members.length === 1;
  const isSoleAdmin = members.filter(m => m.role === 'admin').length === 1 && isAdmin;
  return {
    isAdmin,
    isSoleMember,
    isSoleAdmin,
    canLeave: !isSoleMember && !isSoleAdmin,
    canDelete: isAdmin && !isSoleMember,
  };
}

function TeamSection({ teamData, currentUserId, onNav }: {
  teamData: TeamSettingsData; currentUserId: string; onNav: (id: string) => void;
}) {
  const memberCount = teamData.members.length;
  const { isAdmin, isSoleMember, canLeave, canDelete } = computeTeamCaps(teamData.members, currentUserId);
  const isMobile = useIsMobile();

  const rename = useRenameTeam();
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(teamData.name);
  const [confirmMode, setConfirmMode] = useState<null | 'leave' | 'delete'>(null);

  const handleRename = () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === teamData.name) { setEditing(false); return; }
    rename.mutate({ teamId: teamData.id, name: trimmed }, { onSuccess: () => setEditing(false) });
  };

  const dangerTitle: CSSProperties = { fontSize: 14, fontWeight: 600, color: 'var(--rose)', marginBottom: 4 };
  const dangerDesc: CSSProperties = { fontSize: 12.5, color: 'var(--inkS)', lineHeight: 1.5, marginBottom: 10 };

  return (
    <>
      <div className="card" style={{ overflow: 'hidden' }}>
        <Row label="Team name" sub={`Created ${new Date(teamData.created_at).toLocaleDateString()}`}>
          {editing ? (
            // Phone: the editor wraps — input on its own line, Save/Cancel beneath — instead of
            // cramming a 200px input + two buttons into the space beside the label.
            <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap' }}>
              <input
                value={editName}
                onChange={e => setEditName(e.target.value)}
                maxLength={50}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') { setEditing(false); setEditName(teamData.name); } }}
                style={{
                  width: isMobile ? '100%' : 200, minWidth: 0, padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--hair2)',
                  borderRadius: 8, outline: 'none', color: 'var(--ink)', fontFamily: "'DM Sans', sans-serif", fontSize: 13,
                }}
              />
              <button className="btn btn-secondary sm" disabled={rename.isPending} onClick={handleRename}>{rename.isPending ? 'Saving…' : 'Save'}</button>
              <button className="btn btn-secondary sm" onClick={() => { setEditing(false); setEditName(teamData.name); }}>Cancel</button>
            </span>
          ) : (
            <span style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
              <span style={{ color: 'var(--inkB)', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{teamData.name}</span>
              {isAdmin && <button className="chip" style={{ flexShrink: 0 }} onClick={() => { setEditName(teamData.name); setEditing(true); }}>Rename</button>}
            </span>
          )}
        </Row>
        <Row label="Your role">
          <span style={{
            fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: '.14em',
            textTransform: 'uppercase', color: isAdmin ? 'var(--acc)' : 'var(--inkS)',
          }}>{isAdmin ? 'Admin' : 'Member'}</span>
        </Row>
        <Row label="Members" sub={`${memberCount} ${memberCount === 1 ? 'person' : 'people'} on this team. Manage roster & invites in People.`} last>
          <button className="btn btn-secondary sm" style={{ flexShrink: 0 }} onClick={() => onNav('people')}>Open People <span className="arr">→</span></button>
        </Row>
      </div>
      {rename.isError && <div style={ERROR_BANNER}>Failed to rename team. Try again.</div>}

      {!isAdmin && (
        <div className="section-note" style={{ marginTop: 12 }}>Only admins can manage the team.</div>
      )}

      {isSoleMember && (
        <div className="section-note" style={{ marginTop: 12 }}>
          This is your personal team - you’re its only member. Invite a teammate in People to start sharing context; a personal team can’t be left or deleted, but you can rename it.
        </div>
      )}

      {(canLeave || canDelete) && (
        <div className="danger-zone" style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {canLeave && (
            <div>
              <div style={dangerTitle}>Leave team</div>
              <div style={dangerDesc}>Leave this team. Your conversations will no longer be shared with its members.</div>
              <button className="btn btn-secondary sm" style={ROSE_BTN} onClick={() => setConfirmMode('leave')}>Leave team</button>
            </div>
          )}
          {canDelete && (
            <div style={canLeave ? { borderTop: '1px solid color-mix(in srgb, var(--rose) 18%, transparent)', paddingTop: 16 } : undefined}>
              <div style={dangerTitle}>Delete team</div>
              <div style={dangerDesc}>Permanently delete this team and remove all members. This cannot be undone.</div>
              <button className="btn btn-secondary sm" style={ROSE_BTN} onClick={() => setConfirmMode('delete')}>Delete team</button>
            </div>
          )}
        </div>
      )}

      {confirmMode && (
        <TeamDangerDialog
          mode={confirmMode}
          teamId={teamData.id}
          currentUserId={currentUserId}
          onNav={onNav}
          onClose={() => setConfirmMode(null)}
        />
      )}
    </>
  );
}

// ─── Surface ───

export function SettingsSurface({
  teamData, currentUserId, userEmail, theme, onToggleTheme, onNav,
}: SettingsSurfaceProps) {
  return (
    <div className="v2-content ov-surface">
      <div className="wrap">
        <Masthead />
        <p className="sub">Your account, privacy, and team - managed in one place.</p>

        <SectionHeader>Account</SectionHeader>
        <AccountSection
          userEmail={userEmail}
          theme={theme}
          onToggleTheme={onToggleTheme}
        />

        <SectionHeader>Privacy</SectionHeader>
        <PrivacySection onNav={onNav} />

        <SectionHeader>Team</SectionHeader>
        <TeamSection teamData={teamData} currentUserId={currentUserId} onNav={onNav} />
      </div>
    </div>
  );
}
